/**
 * Shared date formatting utilities.
 * Consolidates duplicate toLocaleDateString patterns across the codebase.
 */

import type { HourCycle } from "@salestrak/ui";

// The viewer's clock, published by AppCalendarProvider from the timeFormat
// preference. A module value rather than context because these formatters
// are plain functions called from render, sort and copy paths alike.
let hourCycle: HourCycle = 12;

export function setHourCycle(next: HourCycle): void {
  hourCycle = next;
}

export function getHourCycle(): HourCycle {
  return hourCycle;
}

/** `toLocaleTimeString` options for the viewer's clock: "2:30 PM" on 12,
 *  "14:30" on 24. */
function clockOptions(): Intl.DateTimeFormatOptions {
  return hourCycle === 24
    ? { hour: "2-digit", minute: "2-digit", hour12: false }
    : { hour: "numeric", minute: "2-digit" };
}

/** "Mar 15, 2026" */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Mar 15, 2026" — always interprets the date in UTC (for date-only values). */
export function formatDateUtc(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "Mar 15" */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/** "Mar 15, 2:30 PM" */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", clockOptions());
  return `${date}, ${time}`;
}

/** "Jul 16, 2026, 2:30 PM" — the unabridged timestamp every abridged date
 *  label expands to on hover (`DateText`, the library `date` column). One
 *  definition: the library owns it for its own column helper. */
export { formatFullDateTime } from "@salestrak/ui";

/** "2:30 PM" — or "14:30" on the viewer's 24-hour clock. */
export function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  return new Date(dateStr).toLocaleTimeString("en-US", clockOptions());
}

/** "Today" / "Yesterday" / "Apr 17" — relative day label for run timestamps. */
export function relativeDay(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Today" / "Yesterday" / "Sep 11" for a DATE-ONLY value ("2026-09-11").
 *  `new Date("2026-09-11")` is UTC midnight, which `relativeDay` would read
 *  as the evening before in every western time zone; this compares the
 *  value's UTC calendar day with the viewer's local calendar day. */
export function relativeDayUtc(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const entryDay = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - entryDay) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** "September 2026" — always interprets the date in UTC (for date-only values). */
export function formatMonthYearUtc(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "Today · 2:30 PM" — the run-row timestamp. */
export function relativeDayTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return `${relativeDay(dateStr)} · ${formatTime(dateStr)}`;
}

/** "6/3, 2:30 PM" — compact note timestamp (numeric month/day, no year).
 *  Shared by the activity feed and the kanban hover preview. */
export function formatNoteDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "---";
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}, ${formatTime(dateStr)}`;
}
